import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

const HERO = "/hero_v2.jpg";
const CAT_BAGS = "https://images.unsplash.com/photo-1594638963668-52eb9798e8ca?w=900";
const CAT_ACC = "https://images.unsplash.com/photo-1629736329185-086161cda231?w=900";
const CAT_CLO = "https://images.pexels.com/photos/30723802/pexels-photo-30723802.jpeg";
const ARTISAN = "https://images.pexels.com/photos/7585231/pexels-photo-7585231.jpeg";

export default function Home() {
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } })
      .then((r) => setFeatured(r.data.slice(0, 4)))
      .catch(() => {});
  }, []);

  return (
    <div data-testid="home-page">
      {/* Hero — editorial split */}
      <section className="relative bg-alimi-bg overflow-hidden">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16 py-16 lg:py-24 items-center min-h-[88vh]">
          {/* Text */}
          <div className="lg:col-span-6 order-2 lg:order-1 fade-in-up">
            <span className="overline">Collezione Inverno 2026</span>
            <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl leading-[0.95] mt-6 tracking-tight text-alimi-ink">
              Creazioni<br />che <em className="italic text-alimi-gold">raccontano</em>.
            </h1>
            <div className="alimi-divider mt-8" />
            <p className="text-base text-alimi-inkSoft mt-7 max-w-md leading-relaxed">
              Borse, accessori e capi unici lavorati a mano all'uncinetto.
              Ogni pezzo è una storia di tempo, gesti e materia.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <Link to="/shop" data-testid="hero-shop-btn" className="btn-primary">
                Scopri la collezione <ArrowRight size={14} />
              </Link>
              <Link to="/about" data-testid="hero-about-btn" className="btn-outline">
                La nostra storia
              </Link>
            </div>
          </div>

          {/* Image */}
          <div className="lg:col-span-6 order-1 lg:order-2 relative fade-in-up delay-1">
            <div className="relative aspect-[4/5] lg:aspect-[5/6] overflow-hidden bg-alimi-surface2">
              <img
                src={HERO}
                alt="Filati pastello Alimì"
                className="w-full h-full object-cover"
              />
            </div>
            {/* Decorative accent */}
            <div className="hidden lg:block absolute -bottom-6 -left-6 w-24 h-24 border border-alimi-gold pointer-events-none" />
            <div className="hidden lg:block absolute -top-4 -right-4 text-alimi-gold font-serif italic text-xl tracking-wide rotate-[-4deg]">
              fatto a mano
            </div>
          </div>
        </div>
      </section>

      {/* Editorial intro */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5 lg:col-start-2">
          <span className="overline">Atelier Italiano</span>
          <h2 className="font-serif text-4xl sm:text-5xl leading-tight mt-5 tracking-tight">
            Un filo,<br />una storia,<br /><em className="italic">un'emozione.</em>
          </h2>
        </div>
        <div className="lg:col-span-5">
          <p className="text-base text-alimi-inkSoft leading-relaxed">
            Da Alimì ogni creazione nasce lentamente, con cura artigiana e materiali scelti.
            Borse intrecciate al gesto antico dell'uncinetto, accessori che parlano di delicatezza
            e capi pensati per essere unici, come chi li indossa.
          </p>
          <div className="alimi-divider mt-8" />
        </div>
      </section>

      {/* Categories */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { cat: "borse", title: "Borse", img: CAT_BAGS },
            { cat: "accessori", title: "Accessori", img: CAT_ACC },
            { cat: "abbigliamento", title: "Abbigliamento", img: CAT_CLO },
          ].map((c, i) => (
            <Link
              key={c.cat}
              to={`/shop/${c.cat}`}
              data-testid={`category-${c.cat}`}
              className="group relative aspect-[3/4] overflow-hidden block fade-in-up"
              style={{ animationDelay: `${0.1 * i}s` }}
            >
              <img src={c.img} alt={c.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/55 via-black/10 to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-7 text-white">
                <div className="overline text-alimi-rose">Esplora</div>
                <div className="font-serif text-3xl mt-2 flex items-center gap-3 group-hover:gap-5 transition-all">
                  {c.title} <ArrowRight size={20} />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured */}
      {featured.length > 0 && (
        <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-28">
          <div className="flex items-end justify-between mb-12">
            <div>
              <span className="overline">In evidenza</span>
              <h2 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight">Pezzi unici</h2>
            </div>
            <Link to="/shop" className="hidden sm:inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] link-underline">
              Vedi tutto <ArrowRight size={14} />
            </Link>
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
            {featured.map((p, i) => <ProductCard key={p.id} product={p} idx={i} />)}
          </div>
        </section>
      )}

      {/* Su Misura CTA */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 mt-12">
        <Link
          to="/su-misura"
          data-testid="home-sumisura-cta"
          className="group relative block overflow-hidden bg-alimi-ink text-white p-12 lg:p-16"
        >
          <div className="relative z-10 grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <span className="overline text-alimi-rose">Su Misura</span>
              <h3 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">
                Un'idea unica?<br /><em className="italic text-alimi-rose">La creiamo per te.</em>
              </h3>
            </div>
            <div className="lg:text-right">
              <p className="text-sm text-white/75 leading-relaxed lg:max-w-md lg:ml-auto">
                Borse, accessori e capi personalizzati su tua richiesta.
                Raccontaci la tua idea: la trasformeremo in un pezzo unico.
              </p>
              <span className="inline-flex items-center gap-2 mt-7 text-[0.78rem] tracking-[0.2em] uppercase border-b border-alimi-rose pb-1 group-hover:gap-4 transition-all">
                Richiedi su misura <ArrowRight size={14} />
              </span>
            </div>
          </div>
        </Link>
      </section>

      {/* Artisan story */}
      <section className="bg-alimi-surface2 py-28 mt-20">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div className="aspect-[4/5] overflow-hidden">
            <img src="/hero_atelier.jpg" alt="Atelier Alimì" className="w-full h-full object-cover" />
          </div>
          <div>
            <span className="overline">La nostra filosofia</span>
            <h2 className="font-serif text-4xl sm:text-5xl mt-4 tracking-tight leading-tight">
              Lentezza,<br />cura, <em className="italic">poesia.</em>
            </h2>
            <p className="text-base text-alimi-inkSoft leading-relaxed mt-7 max-w-lg">
              Crediamo nel valore del fatto a mano. Ogni borsa, ogni cappello, ogni capo
              porta con sé il tempo dedicato a crearlo — un tempo prezioso che restituisce
              autenticità a ciò che indossi.
            </p>
            <Link to="/about" className="btn-outline mt-8" data-testid="story-about-btn">
              Scopri la storia
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
